import { ClockCircleOutlined } from '@ant-design/icons'
import { Horizontal, MomentDatePicker, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Form, Select } from 'antd'
import moment from 'moment'
import React, { useEffect, useMemo } from 'react'
import type { OrderRequestSelectedSite } from './types'
import { isWindowDisabled, formatWindowOption } from './util'

/// Custom delivery window selector for when the windows are explicitly defined by the BE

type DeliveryWindowBySelectionProps = {
  selectedSite: OrderRequestSelectedSite | null
  selectedDate: string | null
  selectedWindow: string | null
}

export function DeliveryWindowBySelection({
  selectedSite,
  selectedDate,
  selectedWindow,
}: DeliveryWindowBySelectionProps) {
  const earliestSelectableTime = selectedSite
    ? moment(selectedSite.order_request_window_start).tz(selectedSite.timezone)
    : null

  const selectedSiteTimezone = selectedSite?.timezone?.replace('_', ' ') ?? 'N/A'

  const initialWindow = useMemo(() => {
    if (!selectedSite) return null
    const firstValidWindow = selectedSite?.fixed_windows.find(
      (window) => !isWindowDisabled(window, selectedDate, selectedSite)
    )
    if (!firstValidWindow) return null
    return firstValidWindow && selectedSite ? formatWindowOption(firstValidWindow, selectedSite.timezone, true) : null
  }, [selectedSite, selectedDate])

  const windowOptions = useMemo(
    () =>
      selectedSite?.fixed_windows.map((window) => {
        const isDisabled = isWindowDisabled(window, selectedDate, selectedSite)
        return formatWindowOption(window, selectedSite.timezone, isDisabled)
      }),
    [selectedSite, selectedDate]
  )

  const errorMessage = useMemo(() => {
    if (!selectedSite) return null
    const isDisabledWindow = windowOptions?.find((option) => option.value === selectedWindow)?.disabled
    if (isDisabledWindow) {
      return 'Delivery window invalid. Please select a valid delivery window.'
    }
    return null
  }, [selectedWindow, windowOptions, selectedSite])

  const disabledDate = (current: moment.Moment) =>
    selectedSite ? current.isBefore(earliestSelectableTime, 'day') : current.isBefore(moment().startOf('day'))

  return (
    <>
      <Horizontal className='pt-4 pb-1 full-height-width' alignItems='center' justifyContent='space-between'>
        <Vertical>
          <Texto align='left'>Delivery Window</Texto>
        </Vertical>
        <Horizontal>
          <Form.Item className='mb-0 mr-1' name='fromDate' rules={[{ required: true, message: 'Date is required.' }]}>
            <MomentDatePicker
              disabledDate={disabledDate}
              placeholder='Select Date'
              format='MMM D, YYYY'
              disabled={selectedSite === null}
            />
          </Form.Item>
          <Form.Item
            className='mb-0'
            name='selectedWindow'
            initialValue={initialWindow}
            rules={[{ required: true, message: 'Window is required.' }]}
          >
            <Select
              options={windowOptions}
              placeholder='Select Window'
              suffixIcon={<ClockCircleOutlined />}
              showSearch
              optionLabelProp='display'
              disabled={selectedSite === null}
            />
          </Form.Item>
        </Horizontal>
      </Horizontal>
      <Texto appearance='error' align='right'>
        {errorMessage}
      </Texto>
      <Vertical verticalCenter alignItems='flex-end' className='p-1'>
        <Texto align='right' className='p-1'>
          Delivery Window uses Site's Timezone ({selectedSiteTimezone})
        </Texto>
      </Vertical>
    </>
  )
}
